import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

import { interval, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ThemeService } from './theme.service';

const FAVICON_DEFAULT_ANIMATION_DELAY = 180 * 1000; // 3 min

@Injectable({
  providedIn: 'root',
})
export class FaviconService {
  readonly #document = inject(DOCUMENT);
  readonly #theme = inject(ThemeService);

  private linkElement = signal<HTMLLinkElement>(this.#getOrCreateLinkElement());
  private currentIndex = signal(0);

  private lightModeFavicons = ['light-mode-favicon-indicator.png', 'light-mode-favicon.png'];
  private darkModeFavicons = ['dark-mode-favicon-indicator.png', 'dark-mode-favicon.png'];

  private blinkSubscription: Subscription | null = null;

  threeDsNotificationsReceived(): void {
    this.#document.addEventListener('visibilitychange', this.#visibilityChange.bind(this));

    if (!this.#document.hasFocus()) {
      this.#runNotificationsIdentification();
    }
  }

  #visibilityChange(): void {
    if (this.#document.visibilityState === 'visible') {
      this.#clearAndReset();
    }
  }

  #runNotificationsIdentification(): void {
    const blink$ = interval(2500);
    const stop$ = timer(FAVICON_DEFAULT_ANIMATION_DELAY);

    this.blinkSubscription = blink$.pipe(takeUntil(stop$)).subscribe(() => this.#updateFavIcon());

    stop$.subscribe(() => this.#clearAndReset());
  }

  #updateFavIcon(): void {
    this.#setFavicon(this.#theme.isDarkTheme ? this.lightModeFavicons[this.currentIndex()] : this.darkModeFavicons[this.currentIndex()]);

    this.currentIndex.update(
      index => (index + 1) % (this.#theme.isDarkTheme ? this.darkModeFavicons.length : this.lightModeFavicons.length),
    );
  }

  #resetFavicon(): void {
    this.#setFavicon(this.#theme.isDarkTheme ? 'light-mode-favicon.png' : 'dark-mode-favicon.png');
  }

  #setFavicon(iconUrl: string): void {
    this.linkElement().href = iconUrl;
  }

  #clearAndReset(): void {
    this.blinkSubscription?.unsubscribe();
    this.blinkSubscription = null;
    this.#document.removeEventListener('visibilitychange', this.#visibilityChange);

    this.#resetFavicon();
  }

  #getOrCreateLinkElement(): HTMLLinkElement {
    return (
      this.#document.querySelector<HTMLLinkElement>("link[rel~='icon'][type='image/png']") ??
      Object.assign(this.#document.createElement('link'), {
        rel: 'icon',
        type: 'image/png',
        href: '',
        parentElement: this.#document.head.appendChild,
      })
    );
  }
}
